import { useEffect } from 'react'
import { useNotificationFeed } from '@/hooks/queries/useNotificationFeedQuery'
import { useNotificationReadSession } from '@/hooks/queries/useNotificationReadSession'
import { isTauri } from '@/lib/utils/tauri'
import { browserNotificationService } from '@/services/browserNotificationService'

/**
 * Desktop-only: mirrors the header notification bell badge onto the macOS dock
 * icon.
 *
 * This reuses the existing notification feed query (same query key as
 * NotificationCenter, so React Query dedupes it — no extra request) and the
 * same precedence the bell uses: action-required first, mentions as the
 * fallback. Zero clears the badge, as does unmounting the app shell.
 */
export function DockBadgeController() {
  const session = useNotificationReadSession()
  const { data: feed } = useNotificationFeed()

  const actionCount = feed?.counts.actionRequired ?? 0
  const mentionCount = feed?.counts.mentions ?? 0
  const badgeCount = actionCount > 0 ? actionCount : mentionCount

  useEffect(() => {
    if (!isTauri()) return

    let active = true
    const isCurrent = () => {
      try {
        session.assertUnchanged()
        return active
      } catch {
        return false
      }
    }
    void browserNotificationService.setBadgeCount(badgeCount, isCurrent)
    return () => {
      active = false
    }
  }, [badgeCount, session])

  useEffect(() => {
    if (!isTauri()) return

    return () => {
      void browserNotificationService.clearBadgeCount(() => {
        try {
          session.assertUnchanged()
          return true
        } catch {
          return false
        }
      })
    }
  }, [session])

  return null
}
